/**
 * /versionsbuilt — public build-provenance page.
 *
 * Purpose: prove that dev / QA / prod run the *same* front-end artifact. The
 * build metadata (version, git commit, build timestamp) is inlined at build
 * time and is byte-identical across environments because the identical `dist/`
 * is deployed to all three Workers — only the runtime config.json differs.
 * Open https://<env>.karexia.com/versionsbuilt on each env and compare the
 * "Build fingerprint": if it matches, the environments are on the same build.
 *
 * Public route (no auth) so the version can be checked even if login is down.
 */
import { type ReactNode, useEffect, useState } from 'react';

interface RuntimeConfig {
  env?: string;
  apiBaseUrl?: string;
  wsUrl?: string;
  keycloak?: { authority?: string; clientId?: string };
}

interface BackendHealth {
  status?: string;
  version?: string;
  timestamp?: string;
  uptime_seconds?: number;
}

const META = __BUILD_META__;

function fmtUTC(iso: string): string {
  if (!iso || iso === 'unknown') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`
  );
}

function realmOf(authority?: string): string {
  const m = authority?.match(/\/realms\/([^/]+)/);
  return m ? m[1] : '—';
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm font-medium text-muted">{label}</span>
      <span
        className={`text-sm text-foreground ${mono ? 'font-mono' : ''} break-all sm:text-right`}
      >
        {value}
      </span>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-token border border-border bg-surface p-4 shadow-elevation-1">
      <h2 className="mb-2 text-base font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

export function VersionsBuiltPage() {
  const [cfg, setCfg] = useState<RuntimeConfig | null>(null);
  const [cfgErr, setCfgErr] = useState<string | null>(null);
  const [health, setHealth] = useState<BackendHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/config.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`config.json ${r.status}`))))
      .then((c: RuntimeConfig) => {
        if (!alive) return;
        setCfg(c);
        if (c.apiBaseUrl) {
          fetch(`${c.apiBaseUrl.replace(/\/$/, '')}/health`, { cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`health ${r.status}`))))
            .then((h: BackendHealth) => alive && setHealth(h))
            .catch((e: Error) => alive && setHealthErr(e.message));
        }
      })
      .catch((e: Error) => alive && setCfgErr(e.message));
    return () => {
      alive = false;
    };
  }, []);

  const env = cfg?.env ?? '—';
  const fingerprint = `${META.version}+${META.gitCommit}@${META.buildTime}`;

  return (
    <div className="min-h-screen bg-surface-2 px-4 py-8 text-foreground">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold">Versions &amp; Build</h1>
            <p className="mt-1 text-sm text-muted">
              PubSocialNet · Karexia — build provenance for dev / QA / prod parity.
            </p>
          </div>
          <span className="rounded-token bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
            env: {env}
          </span>
        </header>

        <Card title="Build fingerprint (compare across environments)">
          <div className="rounded-token bg-surface-2 p-3">
            <code className="block break-all font-mono text-sm text-foreground">{fingerprint}</code>
          </div>
          <p className="mt-2 text-xs text-muted">
            Identical on dev, QA and prod ⇒ all three run the same artifact. Only the{' '}
            <code className="font-mono">env</code> label and config below differ per deployment.
          </p>
        </Card>

        <Card title="Front-end build">
          <Row label="Version" value={META.version} mono />
          <Row label="Build date &amp; time" value={fmtUTC(META.buildTime)} mono />
          <Row label="Build timestamp (ISO)" value={META.buildTime} mono />
          <Row label="Git commit" value={META.gitCommit} mono />
          <Row label="Git commit (full)" value={META.gitCommitFull} mono />
          <Row label="Git branch" value={META.gitBranch} mono />
          <Row label="Commit date" value={fmtUTC(META.gitCommitDate)} mono />
          <Row
            label="Working tree"
            value={META.gitDirty ? 'dirty (uncommitted changes at build)' : 'clean'}
          />
        </Card>

        <Card title="Runtime configuration (per environment)">
          {cfgErr ? (
            <p className="text-sm text-danger">config.json error: {cfgErr}</p>
          ) : cfg ? (
            <>
              <Row label="Environment" value={env} />
              <Row label="API base URL" value={cfg.apiBaseUrl ?? '—'} mono />
              <Row label="WebSocket URL" value={cfg.wsUrl ?? '—'} mono />
              <Row label="Keycloak realm" value={realmOf(cfg.keycloak?.authority)} mono />
              <Row label="Keycloak authority" value={cfg.keycloak?.authority ?? '—'} mono />
              <Row label="Keycloak client" value={cfg.keycloak?.clientId ?? '—'} mono />
            </>
          ) : (
            <p className="text-sm text-muted">Loading config…</p>
          )}
        </Card>

        <Card title="Back-end (API /health)">
          {healthErr ? (
            <p className="text-sm text-danger">unreachable: {healthErr}</p>
          ) : health ? (
            <>
              <Row label="Status" value={health.status ?? '—'} />
              <Row label="API version" value={health.version ?? '—'} mono />
              <Row label="Server time" value={health.timestamp ? fmtUTC(health.timestamp) : '—'} mono />
              {typeof health.uptime_seconds === 'number' && (
                <Row label="Uptime" value={`${Math.floor(health.uptime_seconds / 60)} min`} />
              )}
            </>
          ) : (
            <p className="text-sm text-muted">Checking backend…</p>
          )}
        </Card>

        <footer className="pt-2 text-center text-xs text-muted">
          Generated at render time · {fmtUTC(new Date().toISOString())}
        </footer>
      </div>
    </div>
  );
}

export default VersionsBuiltPage;
